/* ── Album page state management ─────────────────────────────────────── */
// Escape handling lives in StickerAlbum.js as a global keydown listener.

export const initialState = {
  // 'MY_ALBUM' | 'USER_ALBUM' | 'TRADE_MATCH' | 'TRADE_REVIEW' | 'TRADE_DETAIL' | 'OFFER_BUILDER' | 'OFFER_REVIEW'
  // OFFER_BUILDER: armar oferta pública desde el álbum mismo (selecciono mis
  // duplicadas como "doy" y mis faltantes como "pido", sin contraparte).
  // OFFER_REVIEW: revisar una oferta propia (pintar las cartas ofrecidas/pedidas).
  viewMode: 'MY_ALBUM',

  selected: new Set(),

  browsingUser: null,
  theirStickers: [],

  activeTrade: null,
  tradeBuilder: { offering: new Set(), requesting: new Set() },

  // Selección "puente" entre MY_ALBUM y TRADE_MATCH:
  // cuando el usuario clickea un match, guardamos su selección original;
  // al entrar a TRADE_MATCH, splitea automáticamente en offering/requesting.
  pendingSelection: null,

  sidebarCollapsed: localStorage.getItem('album-sidebar-collapsed') === 'true',
  gridColumns: 30,

  // Undo/redo de acciones mutantes (Agregar / Eliminar / Quick-Add / Import).
  // Cada entry guarda { before, after, selectionBefore, label }; before/after son
  // base64 del album, selectionBefore es un Array de números.
  undoStack: [],
  redoStack: [],
};

const UNDO_LIMIT = 20;

export function albumReducer(state, action) {
  switch (action.type) {

    /* ── Selection ──────────────────────────────────────────────────── */
    case 'TOGGLE_SELECT': {
      const next = new Set(state.selected);
      if (next.has(action.n)) next.delete(action.n); else next.add(action.n);
      return { ...state, selected: next };
    }
    // Agrega varios sticker numbers a la selección sin togglear (usado por
    // QuickAddBar — evita que tipear dos veces el mismo código deseleccione).
    case 'ADD_TO_SELECTION': {
      const next = new Set(state.selected);
      for (const n of action.nums || []) next.add(n);
      return { ...state, selected: next };
    }
    case 'CLEAR_SELECTION':
      return { ...state, selected: new Set() };

    /* ── View transitions ──────────────────────────────────────────── */
    case 'VIEW_MY_ALBUM':
      return {
        ...state,
        viewMode: 'MY_ALBUM',
        browsingUser: null,
        theirStickers: [],
        activeTrade: null,
        tradeBuilder: { offering: new Set(), requesting: new Set() },
        selected: new Set(),
        pendingSelection: null,
      };

    case 'VIEW_USER_ALBUM':
      return {
        ...state,
        viewMode: 'USER_ALBUM',
        browsingUser: action.user,
        theirStickers: action.stickers,
        activeTrade: null,
        tradeBuilder: { offering: new Set(), requesting: new Set() },
        selected: new Set(),
        // Si viene `pendingSelection` en el payload, se guarda; sino se resetea.
        pendingSelection: action.pendingSelection && action.pendingSelection.size > 0
          ? new Set(action.pendingSelection)
          : null,
      };

    case 'VIEW_TRADE_MATCH':
      return { ...state, viewMode: 'TRADE_MATCH', selected: new Set() };

    case 'VIEW_OFFER_BUILDER':
      return {
        ...state,
        viewMode: 'OFFER_BUILDER',
        browsingUser: null,
        theirStickers: [],
        activeTrade: null,
        tradeBuilder: { offering: new Set(), requesting: new Set() },
        selected: new Set(),
        pendingSelection: null,
      };

    case 'CLEAR_PENDING_SELECTION':
      return { ...state, pendingSelection: null };

    case 'VIEW_TRADE_REVIEW':
      return {
        ...state,
        viewMode: 'TRADE_REVIEW',
        activeTrade: action.trade,
        selected: new Set(),
      };

    case 'VIEW_TRADE_DETAIL':
      return {
        ...state,
        viewMode: 'TRADE_DETAIL',
        activeTrade: action.trade,
        selected: new Set(),
      };

    // Revisar una oferta propia: pinta offered/requested en la grilla.
    // action.offer debe tener { id, offered: number[], requested: number[], status }
    case 'VIEW_OFFER_REVIEW':
      return {
        ...state,
        viewMode: 'OFFER_REVIEW',
        activeTrade: {
          id: action.offer.id,
          youGive: action.offer.offered || [],
          youReceive: action.offer.requested || [],
          status: action.offer.status || 'OPEN',
          _isOffer: true,
        },
        selected: new Set(),
      };

    case 'REFRESH_THEIR_STICKERS':
      // Solo actualiza si seguimos en USER_ALBUM o TRADE_MATCH (mirando a este usuario)
      if (state.viewMode === 'USER_ALBUM' || state.viewMode === 'TRADE_MATCH') {
        return { ...state, theirStickers: action.stickers };
      }
      return state;

    /* ── Trade builder ─────────────────────────────────────────────── */
    case 'TOGGLE_TRADE_OFFER': {
      const next = new Set(state.tradeBuilder.offering);
      if (next.has(action.n)) next.delete(action.n); else next.add(action.n);
      return { ...state, tradeBuilder: { ...state.tradeBuilder, offering: next } };
    }
    case 'TOGGLE_TRADE_REQUEST': {
      const next = new Set(state.tradeBuilder.requesting);
      if (next.has(action.n)) next.delete(action.n); else next.add(action.n);
      return { ...state, tradeBuilder: { ...state.tradeBuilder, requesting: next } };
    }
    case 'SET_TRADE_BUILDER':
      // Bulk-set ambos sets. Lo usa el botón "Intercambio rápido" del bottom
      // bar para pre-cargar la propuesta óptima sin que el user toque cada
      // figurita una por una.
      return {
        ...state,
        tradeBuilder: {
          offering: new Set(action.offering || []),
          requesting: new Set(action.requesting || []),
        },
      };
    case 'CANCEL_TRADE':
      return {
        ...state,
        tradeBuilder: { offering: new Set(), requesting: new Set() },
        activeTrade: null,
        viewMode: state.viewMode === 'OFFER_BUILDER' ? 'MY_ALBUM'
                : state.browsingUser ? 'TRADE_MATCH' : 'MY_ALBUM',
      };

    /* ── UI chrome ─────────────────────────────────────────────────── */
    case 'TOGGLE_SIDEBAR': {
      const next = !state.sidebarCollapsed;
      localStorage.setItem('album-sidebar-collapsed', String(next));
      return { ...state, sidebarCollapsed: next };
    }
    // TODO: Grid column selector (presets: 25, 30, 35) — UI ready but disabled for now
    // TODO: Show groups toggle — ALBUM_SECTIONS data in albumData.js, sectioned AlbumGrid version was built (see git history)
    // Add a toggle button in AlbumActionBar to enable/disable section headers
    case 'SET_GRID_COLUMNS': {
      const PRESETS = [25, 30, 35];
      const col = PRESETS.includes(action.columns) ? action.columns : 30;
      localStorage.setItem('album-grid-columns', String(col));
      return { ...state, gridColumns: col };
    }

    /* ── Undo / Redo ───────────────────────────────────────────────── */
    // Después de aplicar una mutación: pushea entry, vacía redo, deja la
    // selección en `selectionAfter` (default vacío). Para acciones desde la
    // selección (Agregar/Eliminar) selectionAfter = []. Para Quick-Add la
    // selección manual no debería tocarse, así que pasa selectionAfter =
    // selectionBefore.
    case 'PUSH_UNDO': {
      const next = [...state.undoStack, action.entry];
      while (next.length > UNDO_LIMIT) next.shift();
      const sel = action.entry.selectionAfter || [];
      return {
        ...state,
        undoStack: next,
        redoStack: [],
        selected: new Set(sel),
      };
    }
    // Mueve el tope del undoStack a redoStack y restaura la selección
    // que estaba antes de la acción. La restauración del album ocurre fuera
    // (StickerAlbum.js llama a updateMyAlbum con entry.before y refleja en setMyBase64).
    case 'UNDO_POP': {
      const stack = [...state.undoStack];
      const entry = stack.pop();
      if (!entry) return state;
      return {
        ...state,
        undoStack: stack,
        redoStack: [...state.redoStack, entry],
        selected: new Set(entry.selectionBefore || []),
      };
    }
    // Reaplica la última acción deshecha: restaura selectionAfter (default
    // vacío para Add/Remove; igual a selectionBefore para Quick-Add).
    case 'REDO_POP': {
      const stack = [...state.redoStack];
      const entry = stack.pop();
      if (!entry) return state;
      return {
        ...state,
        undoStack: [...state.undoStack, entry],
        redoStack: stack,
        selected: new Set(entry.selectionAfter || []),
      };
    }

    default:
      return state;
  }
}
